import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThreadMenuContent } from "@/components/Chat/header/ThreadMenu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { WorkspaceCreateDialog } from "@/components/workspace";
import { useSidebarDndContext } from "@/contexts/SidebarDndProvider";
import {
  ProjectDeleteDialog,
  ProjectRenameDialog,
  ThreadRenameDialog,
} from "@/features/sidebar/projects/dialogs";
import { useProjectActions } from "@/features/sidebar/projects/hooks/useProjectActions";
import { useProjects } from "@/features/sidebar/projects/hooks/useProjects";
import { useThreadActions } from "@/features/sidebar/projects/hooks/useThreadActions";
import { useThreadNewTracking } from "@/features/sidebar/projects/hooks/useThreadNewTracking";
import { useWorkspaceThreadsContext } from "@/contexts/WorkspaceThreadsContext";
import {
  isDefaultTitle,
  isPersonalProject,
} from "@/features/sidebar/projects/utils";
import { useNotificationRealtime } from "@/hooks/useNotificationRealtime";
import { useToast } from "@/hooks/useToast";
import { SupabaseWorkspaceUtils } from "@/lib/db/utils/supabase/workspace";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";
import { dropdownMenuItemDestructiveClasses } from "@/lib/constants/menu-styles";
import useAuthStore from "@/store/authStore";
import { useThreadStore } from "@/store/index";
import { useDndContext, useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Box,
  ChevronDown,
  ChevronRight,
  Edit,
  Folder,
  FolderOpen,
  MoreHorizontal,
  Palette,
  Plus,
  Trash2,
  Users,
} from "lucide-react";
import ColorIconChooser from "@/components/ui/color-icon-chooser";
import { getWorkspaceIconComponent } from "@/lib/constants/workspace-customization";
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SortableThreadItemInline } from "./SortableThreadItemInline";
import { SortableWorkspaceItem } from "./SortableWorkspaceItem";

function NavProjects() {
  const { isMobile, state: sidebarState } = useSidebar();
  // Show collapsed HoverCard view only on desktop when sidebar is collapsed
  // On mobile, always show inline content in the Sheet drawer
  const isCollapsed = !isMobile && sidebarState === "collapsed";
  const location = useLocation();
  const { workspaceId: urlWorkspaceId } = useParams();
  const { toast } = useToast();

  // Projects section collapse state
  const [isProjectsSectionExpanded, setIsProjectsSectionExpanded] =
    useState(true);

  // Workspace dropdown state
  const [openWorkspaceDropdownId, setOpenWorkspaceDropdownId] = useState(null);

  // ColorIconChooser state - tracks which workspace's chooser is open
  const [colorIconChooserOpenId, setColorIconChooserOpenId] = useState(null);

  // Create project dialog state
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] =
    useState(false);
  const [isCreatingProject, setIsCreatingProject] = useState(false);

  // Hover state for collapsed icon
  const [isHoveringProjects, setIsHoveringProjects] = useState(false);

  // Hover state for expanded section header icon
  const [isHoveringProjectsHeader, setIsHoveringProjectsHeader] =
    useState(false);

  // Controlled state for collapsed HoverCard - close when sidebar expands
  const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);

  // Thread new tracking
  const { markThreadAsNew, isThreadNew } = useThreadNewTracking();

  // Projects data and state
  const {
    workspaces,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    setCurrentWorkspace,
    setWorkspaces,
    expandedWorkspaces,
    setExpandedWorkspaces,
    showAllProjects,
    setShowAllProjects,
    imageFallbacks,
    handleImageError,
    toggleWorkspaceExpansion,
  } = useProjects();

  // Get workspace threads from context (lifted to app-sidebar.jsx)
  const { workspaceThreads, setWorkspaceThreads } =
    useWorkspaceThreadsContext() || {};

  // Project actions
  const {
    creatingChatId,
    isLoading,
    workspaceRenameDialogOpen,
    workspaceToRename,
    newWorkspaceName,
    setNewWorkspaceName,
    isRenamingWorkspace,
    workspaceDeleteDialogOpen,
    workspaceToDelete,
    handleWorkspaceSelect,
    handleCreateChat,
    handleWorkspaceRename,
    handleWorkspaceRenameSubmit,
    handleWorkspaceRenameCancel,
    handleWorkspaceDelete,
    handleConfirmWorkspaceDelete,
    handleCancelWorkspaceDelete,
    handleWorkspaceCustomize,
  } = useProjectActions({
    workspaces,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    setCurrentWorkspace,
    setWorkspaces,
    expandedWorkspaces,
    setExpandedWorkspaces,
    workspaceThreads,
    setWorkspaceThreads,
    markThreadAsNew,
  });

  // Get threads from store
  const { threads, clearCurrentThread } = useThreadStore();
  const navigate = useNavigate();

  // Thread actions
  const {
    openDropdownId,
    threadRenameDialogOpen,
    threadToRename,
    newThreadName,
    isRenamingThread,
    setOpenDropdownId,
    setThreadRenameDialogOpen,
    setNewThreadName,
    handleThreadSelect,
    handleThreadDelete,
    handleThreadRenameSubmit,
    startRenaming,
    handleThreadRenameCancel,
    handleToggleBookmark,
    handleThreadClone,
    handleMoveToProject,
  } = useThreadActions({
    threads,
    workspaceThreads,
    setWorkspaceThreads,
    workspaces,
    setWorkspaces,
    markThreadAsNew,
  });

  // Get user from auth store for subscriptions
  const { user } = useAuthStore();

  // Note: Drag and drop handling is centralized in SidebarDndProvider
  // via useSidebarDndHandlers hook - no need for individual hooks here

  // Handle create project
  const handleCreateWorkspace = async (workspaceData) => {
    setIsCreatingProject(true);
    try {
      const { createWorkspace } = useAuthStore.getState();
      const newWorkspace = await createWorkspace(workspaceData);

      if (newWorkspace) {
        setWorkspaces((prev) => [...prev, newWorkspace]);
        setIsCreateProjectDialogOpen(false);
        toast({
          title: "Success",
          description: "Project created successfully",
        });
        return newWorkspace;
      }
    } catch (error) {
      console.error("Error creating project:", error);
      toast({
        title: "Error",
        description: "Failed to create project. Please try again.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsCreatingProject(false);
    }
  };

  // Workspace subscription ref
  const workspaceChannelRef = useRef(null);

  // Title tracking for animations
  const [previousThreadTitles, setPreviousThreadTitles] = useState(new Map());

  // Expand workspace when URL has workspaceId
  // Skip expansion if prevented (e.g., when navigating via project name click)
  useEffect(() => {
    if (urlWorkspaceId) {
      const preventExpansion = localStorage.getItem(
        "preventWorkspaceExpansion"
      );
      if (preventExpansion === urlWorkspaceId) {
        // Don't expand if this workspace was just navigated to via name click
        return;
      }
      // Use startTransition for URL-based expansion (non-urgent)
      startTransition(() => {
        setExpandedWorkspaces((prev) => ({
          ...prev,
          [urlWorkspaceId]: true,
        }));
      });
    }
  }, [urlWorkspaceId, setExpandedWorkspaces]);

  // Track previous pathname to only expand on navigation, not on drag-and-drop
  const previousPathnameRef = useRef(location.pathname);

  // Expand workspace when thread is selected (only on navigation, not on drag-and-drop)
  useEffect(() => {
    // Only run if pathname actually changed (user navigation)
    const pathnameChanged = previousPathnameRef.current !== location.pathname;
    previousPathnameRef.current = location.pathname;

    if (!pathnameChanged) {
      return; // Skip if pathname didn't change (e.g., during drag-and-drop)
    }

    if (
      !location.pathname.startsWith("/chat/") ||
      location.pathname === "/chat/new"
    ) {
      return;
    }

    const pathParts = location.pathname.split("/");
    const threadIdFromUrl = pathParts[2];

    if (!threadIdFromUrl || threadIdFromUrl === "new") {
      return;
    }

    let threadWorkspaceId = null;
    for (const [workspaceId, wsThreads] of Object.entries(workspaceThreads)) {
      if (wsThreads && wsThreads.some((t) => t.id === threadIdFromUrl)) {
        threadWorkspaceId = workspaceId;
        break;
      }
    }

    if (threadWorkspaceId) {
      // Use startTransition for thread-based expansion (non-urgent)
      startTransition(() => {
        setExpandedWorkspaces((prev) => ({
          ...prev,
          [threadWorkspaceId]: true,
        }));

        if (currentWorkspaceId !== threadWorkspaceId) {
          const workspace = workspaces.find((w) => w.id === threadWorkspaceId);
          if (workspace) {
            setCurrentWorkspaceId(threadWorkspaceId);
            setCurrentWorkspace(workspace);
          }
        }
      });
    }
  }, [
    location.pathname,
    workspaceThreads,
    workspaces,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    setCurrentWorkspace,
    setExpandedWorkspaces,
  ]);

  // Track thread title changes for animation
  useEffect(() => {
    if (!workspaceThreads) return;

    let hasChanges = false;
    const newTitleMap = new Map();

    Object.entries(workspaceThreads).forEach(([workspaceId, threads]) => {
      if (threads && threads.length > 0) {
        threads.forEach((thread) => {
          const titleKey = `${workspaceId}-${thread.id}`;
          const currentTitle = thread.title;
          const previousTitle = previousThreadTitles.get(titleKey);

          newTitleMap.set(titleKey, currentTitle);

          if (
            previousTitle !== undefined &&
            isDefaultTitle(previousTitle) &&
            !isDefaultTitle(currentTitle) &&
            !isThreadNew(thread.id)
          ) {
            markThreadAsNew(thread.id, currentTitle);
          }

          if (previousTitle !== currentTitle) {
            hasChanges = true;
          }
        });
      }
    });

    if (hasChanges) {
      setPreviousThreadTitles(newTitleMap);
    }
  }, [workspaceThreads, isThreadNew, markThreadAsNew, previousThreadTitles]);

  // Workspace subscription for real-time updates
  useEffect(() => {
    if (!user?.id || workspaceChannelRef.current) return;

    try {
      const workspaceChannel = supabase
        .channel(`workspace-updates-${user.id}-${Date.now()}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "workspaces",
          },
          async (payload) => {
            try {
              if (payload.new && payload.new.id) {
                const updatedWorkspace =
                  await SupabaseWorkspaceUtils.getWorkspace(payload.new.id);

                if (updatedWorkspace) {
                  setWorkspaces((prevWorkspaces) =>
                    prevWorkspaces.map((w) =>
                      w.id === updatedWorkspace.id ? updatedWorkspace : w
                    )
                  );
                }
              }
            } catch (error) {
              console.debug("Error handling workspace update:", error);
            }
          }
        )
        .subscribe((status) => {
          if (status !== "SUBSCRIBED") {
            console.debug("Workspace subscription status:", status);
          }
        });

      workspaceChannelRef.current = workspaceChannel;
    } catch (error) {
      console.debug("Error setting up workspace subscription:", error);
    }

    return () => {
      if (workspaceChannelRef.current) {
        try {
          if (supabase && typeof supabase.removeChannel === "function") {
            supabase.removeChannel(workspaceChannelRef.current);
          }
        } catch (error) {
          console.debug(
            "Workspace subscription cleanup completed:",
            error.message
          );
        }
        workspaceChannelRef.current = null;
      }
    };
  }, [setWorkspaces, user?.id]);

  // Notification realtime for workspace thread additions
  useNotificationRealtime({
    enabled: !!user?.id && workspaces.length > 0,
    onInsert: (payload) => {
      try {
        if (payload.new?.type === "workspace_thread_added") {
          const workspaceId = payload.new.workspace_id;
          if (workspaceId) {
            SupabaseWorkspaceUtils.getWorkspace(workspaceId)
              .then((updatedWorkspace) => {
                if (updatedWorkspace) {
                  setWorkspaces((prevWorkspaces) =>
                    prevWorkspaces.map((w) =>
                      w.id === workspaceId ? updatedWorkspace : w
                    )
                  );
                }
              })
              .catch((error) => {
                console.debug(
                  "Error updating workspace after notification:",
                  error
                );
              });
          }
        }
      } catch (error) {
        console.debug("Error handling workspace notification:", error);
      }
    },
  });

  // Handle drag-and-drop between workspaces

  // Get workspace icon or default
  const getWorkspaceIcon = (workspace) => {
    if (workspace?.logo_url && !imageFallbacks[workspace.id]) {
      const WorkspaceLogoIcon = () => (
        <img
          src={workspace.logo_url}
          alt={workspace.name}
          className="h-4 w-4 rounded"
          onError={(e) => handleImageError(workspace.id, e)}
        />
      );
      WorkspaceLogoIcon.displayName = "WorkspaceLogoIcon";
      return WorkspaceLogoIcon;
    }
    return Folder;
  };

  const displayWorkspaces = showAllProjects
    ? workspaces
    : workspaces.slice(0, 5);

  // Memoize workspace threads map for performance
  const workspaceThreadsMap = useMemo(() => {
    const map = {};
    displayWorkspaces.forEach((workspace) => {
      map[workspace.id] = workspaceThreads?.[workspace.id] || [];
    });
    return map;
  }, [displayWorkspaces, workspaceThreads]);

  // Get drag state for all workspaces (hooks must be called at top level)
  const { draggedItemType, activeId, draggedItem, dragSource } =
    useSidebarDndContext();
  const { over } = useDndContext();

  // State for pagination in collapsed view
  const [collapsedProjectsLimit, setCollapsedProjectsLimit] = useState(50);
  const collapsedProjectsScrollRef = useRef(null);

  // Close HoverCard when sidebar expands from collapsed state
  useEffect(() => {
    if (!isCollapsed && isHoverCardOpen) {
      setIsHoverCardOpen(false);
    }
  }, [isCollapsed, isHoverCardOpen]);

  // Handle scroll for loading more projects in collapsed view
  const handleCollapsedProjectsScroll = useCallback(
    (e) => {
      const element = e.currentTarget;
      const isNearBottom =
        element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

      if (isNearBottom && collapsedProjectsLimit < workspaces.length) {
        setCollapsedProjectsLimit((prev) =>
          Math.min(prev + 50, workspaces.length)
        );
      }
    },
    [collapsedProjectsLimit, workspaces.length]
  );

  // Render collapsed icon with hover card
  if (isCollapsed) {
    const collapsedDisplayWorkspaces = workspaces.slice(
      0,
      collapsedProjectsLimit
    );

    return (
      <>
        <SidebarGroup className="w-full min-w-0 max-w-full">
          <SidebarMenu>
            <SidebarMenuItem className="w-full min-w-0 max-w-full">
              <HoverCard
                openDelay={200}
                open={isHoverCardOpen}
                onOpenChange={setIsHoverCardOpen}
              >
                <HoverCardTrigger asChild>
                  <SidebarMenuButton
                    size="icon"
                    className="w-full justify-center"
                    onMouseEnter={() => setIsHoveringProjects(true)}
                    onMouseLeave={() => setIsHoveringProjects(false)}
                  >
                    {isHoveringProjects ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <Box className="h-4 w-4" />
                    )}
                  </SidebarMenuButton>
                </HoverCardTrigger>
                <HoverCardContent
                  side="right"
                  align="start"
                  sideOffset={12}
                  className="w-80 p-3"
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
                        <Box className="h-5 w-5" />
                        Projects
                      </h4>
                      <span className="text-xs text-gray-500 dark:text-white/50">
                        {workspaces.length} projects
                      </span>
                    </div>
                    <div
                      ref={collapsedProjectsScrollRef}
                      onScroll={handleCollapsedProjectsScroll}
                      className="space-y-1 max-h-96 overflow-y-auto"
                    >
                      {collapsedDisplayWorkspaces.map((workspace) => {
                        const WorkspaceIcon = getWorkspaceIcon(workspace);
                        const isExpanded = Boolean(
                          expandedWorkspaces[workspace.id]
                        );

                        // Determine if this workspace should be highlighted (same logic as main view)
                        let isActive = false;
                        if (
                          location.pathname.startsWith(
                            `/projects/${workspace.id}`
                          )
                        ) {
                          isActive = true;
                        } else if (location.pathname.startsWith("/chat/")) {
                          const pathParts = location.pathname.split("/");
                          const threadIdFromUrl = pathParts[2];
                          if (threadIdFromUrl && threadIdFromUrl !== "new") {
                            const currentThread = threads.find(
                              (t) => t.id === threadIdFromUrl
                            );
                            if (currentThread?.workspace_id === workspace.id) {
                              isActive = true;
                            }
                          }
                        }
                        return (
                          <button
                            key={workspace.id}
                            onClick={() => handleWorkspaceSelect(workspace.id)}
                            className={cn(
                              "w-full flex items-center justify-start gap-3 px-3 py-1.5 rounded-xl text-xs font-medium text-left",
                              "transform-gpu hover:scale-[1.02] hover:shadow-md active:scale-[0.98]",
                              "transition-[background-color,transform] duration-200",
                              "text-gray-900 dark:text-white hover:bg-zinc-100 dark:hover:bg-zinc-700/70",
                              isActive && "bg-zinc-100 dark:bg-zinc-700/70"
                            )}
                          >
                            {workspace?.logo_url &&
                            !imageFallbacks[workspace.id] ? (
                              <WorkspaceIcon />
                            ) : workspace?.metadata?.icon ? (
                              (() => {
                                const CustomIcon = getWorkspaceIconComponent(
                                  workspace.metadata.icon
                                );
                                return (
                                  <CustomIcon
                                    className="h-5 w-5 flex-shrink-0"
                                    style={{
                                      color:
                                        workspace?.metadata?.color ||
                                        "currentColor",
                                    }}
                                  />
                                );
                              })()
                            ) : isExpanded ? (
                              <FolderOpen
                                className="h-5 w-5 flex-shrink-0"
                                style={{
                                  color:
                                    workspace?.metadata?.color ||
                                    "currentColor",
                                }}
                              />
                            ) : (
                              <Folder
                                className="h-5 w-5 flex-shrink-0"
                                style={{
                                  color:
                                    workspace?.metadata?.color ||
                                    "currentColor",
                                }}
                              />
                            )}
                            <span
                              className="truncate min-w-0 text-left"
                              title={workspace.name}
                            >
                              {workspace.name}
                            </span>
                          </button>
                        );
                      })}
                      {collapsedProjectsLimit < workspaces.length && (
                        <div className="text-xs text-muted-foreground text-center py-2">
                          Loading more...
                        </div>
                      )}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
        {/* Dialogs */}
        <ProjectRenameDialog
          isOpen={workspaceRenameDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              handleWorkspaceRenameCancel();
            }
          }}
          workspaceToRename={workspaceToRename}
          newWorkspaceName={newWorkspaceName}
          setNewWorkspaceName={setNewWorkspaceName}
          isRenamingWorkspace={isRenamingWorkspace}
          onRename={handleWorkspaceRenameSubmit}
          onCancel={handleWorkspaceRenameCancel}
        />
        <ProjectDeleteDialog
          isOpen={workspaceDeleteDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              handleCancelWorkspaceDelete();
            }
          }}
          workspaceToDelete={workspaceToDelete}
          onConfirm={handleConfirmWorkspaceDelete}
          onCancel={handleCancelWorkspaceDelete}
        />
      </>
    );
  }

  return (
    <>
      <Collapsible
        open={isProjectsSectionExpanded}
        onOpenChange={setIsProjectsSectionExpanded}
        className="group/projects-section w-full min-w-0 max-w-full"
        style={{ maxWidth: "calc(var(--sidebar-width) - 10px)" }}
      >
        <SidebarGroup className="w-full min-w-0 max-w-full">
          <SidebarMenu className="w-full min-w-0 max-w-full">
            <SidebarMenuItem className="w-full min-w-0 max-w-full">
              <div className="relative flex items-center w-full group/projects-header hover:bg-sidebar-accent rounded-md transition-colors">
                <CollapsibleTrigger asChild className="flex-1">
                  <SidebarMenuButton
                    tooltip="Projects"
                    className="w-full min-w-0 max-w-full data-[state=open]:bg-sidebar data-[state=open]:text-sidebar-foreground group-hover/projects-header:bg-sidebar-accent group-hover/projects-header:text-sidebar-accent-foreground"
                    onMouseEnter={() => setIsHoveringProjectsHeader(true)}
                    onMouseLeave={() => setIsHoveringProjectsHeader(false)}
                  >
                    {isHoveringProjectsHeader ? (
                      isProjectsSectionExpanded ? (
                        <ChevronDown className="h-4 w-4 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 flex-shrink-0" />
                      )
                    ) : (
                      <Box className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span className="truncate min-w-0 group-data-[collapsible=icon]:hidden">
                      Projects
                    </span>
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                {/* Create Project Button */}
                <button
                  type="button"
                  className="create-project-button h-6 w-6 flex items-center justify-center bg-transparent rounded-sm opacity-0 group-hover/projects-header:opacity-100 transition-opacity duration-200 hover:bg-sidebar-accent cursor-pointer flex-shrink-0 absolute right-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsCreateProjectDialogOpen(true);
                  }}
                  title="Create new project"
                >
                  <Plus className="h-4 w-4 text-sidebar-foreground/60 group-hover/projects-header:text-sidebar-foreground transition-colors" />
                </button>
              </div>
            </SidebarMenuItem>
          </SidebarMenu>
          <CollapsibleContent>
            <SidebarMenu className="w-full min-w-0 max-w-full">
              <SortableContext
                items={displayWorkspaces.map((w) => w.id)}
                strategy={verticalListSortingStrategy}
              >
                {displayWorkspaces.map((workspace) => {
                  const WorkspaceIcon = getWorkspaceIcon(workspace);
                  // Ensure isExpanded is always a boolean to prevent uncontrolled/controlled warning
                  const isExpanded = Boolean(expandedWorkspaces[workspace.id]);
                  // Use memoized thread lookup for performance
                  const wsThreads = workspaceThreadsMap[workspace.id] || [];

                  // Determine if this workspace should be highlighted
                  // Only highlight if:
                  // 1. We're on the project page for this workspace, OR
                  // 2. We're on a chat page and the current thread belongs to this workspace
                  let isActive = false;
                  if (
                    location.pathname.startsWith(`/projects/${workspace.id}`)
                  ) {
                    // On the project page for this workspace
                    isActive = true;
                  } else if (location.pathname.startsWith("/chat/")) {
                    // On a chat page - check if the thread belongs to this workspace
                    const pathParts = location.pathname.split("/");
                    const threadIdFromUrl = pathParts[2];
                    if (threadIdFromUrl && threadIdFromUrl !== "new") {
                      const currentThread = threads.find(
                        (t) => t.id === threadIdFromUrl
                      );
                      if (currentThread?.workspace_id === workspace.id) {
                        // Thread belongs to this workspace
                        isActive = true;
                      }
                    }
                  }

                  // Check if dragging chat over this workspace (including threads area)
                  // Check multiple conditions: workspace droppable, threads area, or threads within this workspace
                  const isOverWorkspaceDroppable =
                    over &&
                    (over.id === `workspace-drop-${workspace.id}` ||
                      over.id === `workspace-threads-${workspace.id}` ||
                      over.id === workspace.id ||
                      (over.data.current?.type === "workspace" &&
                        over.data.current?.workspaceId === workspace.id));

                  // Check if dragging over a thread that belongs to this workspace
                  const isOverThreadInWorkspace =
                    over &&
                    over.data.current?.type === "chat" &&
                    over.data.current?.workspaceId === workspace.id;

                  // Get the dragged item's workspace ID to check if it's already in this workspace
                  const draggedItemWorkspaceId =
                    draggedItem?.workspace_id || draggedItem?.workspaceId;

                  // Only show drop indicator for chats from the sidebar, and only if the chat is not already in this workspace
                  // Cross-area drag (from project page to sidebar) is not supported
                  const isDraggingChatOverWorkspace =
                    (isOverWorkspaceDroppable || isOverThreadInWorkspace) &&
                    draggedItemType === "chat" &&
                    dragSource === "sidebar" && // Only show for drags from sidebar, not from project page
                    activeId &&
                    draggedItemWorkspaceId !== workspace.id; // Don't show if already in this workspace

                  return (
                    <SortableWorkspaceItem
                      key={workspace.id}
                      workspace={workspace}
                    >
                      <Collapsible
                        asChild
                        open={isExpanded}
                        onOpenChange={(open) => {
                          // Use startTransition for non-urgent state updates to keep UI responsive
                          startTransition(() => {
                            setExpandedWorkspaces((prev) => ({
                              ...prev,
                              [workspace.id]: open,
                            }));
                          });
                        }}
                        className="group/collapsible"
                      >
                        <SidebarMenuItem
                          className="w-full min-w-0 max-w-full"
                          style={{
                            maxWidth: "calc(var(--sidebar-width) - 10px)",
                          }}
                        >
                          <div
                            className={cn(
                              "relative flex items-center gap-0 w-full group/workspace rounded-md transition-colors",
                              isActive
                                ? "bg-sidebar-accent"
                                : "hover:bg-sidebar-accent",
                              isDraggingChatOverWorkspace &&
                                !isExpanded &&
                                "ring-2 ring-purple-400/50 ring-offset-2 ring-offset-sidebar bg-purple-400/10"
                            )}
                          >
                            {/* Drop indicator for closed folders */}
                            {isDraggingChatOverWorkspace && !isExpanded && (
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500 rounded-l-md animate-pulse" />
                            )}

                            {/* Folder icon - with ColorIconChooser for customization */}
                            <ColorIconChooser
                              isOpen={colorIconChooserOpenId === workspace.id}
                              onOpenChange={(open) => {
                                if (!open) setColorIconChooserOpenId(null);
                              }}
                              currentColor={
                                workspace?.metadata?.color || "#f8f9fa"
                              }
                              currentIcon={
                                workspace?.metadata?.icon || "folder"
                              }
                              onColorSelect={(color) => {
                                handleWorkspaceCustomize(workspace.id, {
                                  color: color.value,
                                });
                              }}
                              onIconSelect={(icon) => {
                                handleWorkspaceCustomize(workspace.id, {
                                  icon: icon.value,
                                });
                              }}
                            >
                              <CollapsibleTrigger asChild>
                                <button
                                  type="button"
                                  className={cn(
                                    "h-8 w-8 flex items-center justify-center rounded-md bg-transparent hover:bg-transparent transition-colors flex-shrink-0 group/folder-icon",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-2 focus-visible:relative focus-visible:z-[100]",
                                    isActive && "bg-sidebar-accent"
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  {workspace?.logo_url &&
                                  !imageFallbacks[workspace.id] ? (
                                    <div className="relative h-4 w-4 flex items-center justify-center">
                                      <WorkspaceIcon className="h-4 w-4 opacity-100 group-hover/folder-icon:opacity-0 transition-opacity" />
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 absolute inset-0 opacity-0 group-hover/folder-icon:opacity-100 transition-opacity text-sidebar-foreground/60" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 absolute inset-0 opacity-0 group-hover/folder-icon:opacity-100 transition-opacity text-sidebar-foreground/60" />
                                      )}
                                    </div>
                                  ) : workspace?.metadata?.icon ? (
                                    (() => {
                                      const CustomIcon =
                                        getWorkspaceIconComponent(
                                          workspace.metadata.icon
                                        );
                                      return (
                                        <div className="relative h-4 w-4 flex items-center justify-center">
                                          <CustomIcon
                                            className="h-4 w-4 opacity-100 group-hover/folder-icon:opacity-0 transition-opacity"
                                            style={{
                                              color:
                                                workspace?.metadata?.color ||
                                                "currentColor",
                                            }}
                                          />
                                          {isExpanded ? (
                                            <ChevronDown className="h-4 w-4 absolute inset-0 opacity-0 group-hover/folder-icon:opacity-100 transition-opacity text-sidebar-foreground/60" />
                                          ) : (
                                            <ChevronRight className="h-4 w-4 absolute inset-0 opacity-0 group-hover/folder-icon:opacity-100 transition-opacity text-sidebar-foreground/60" />
                                          )}
                                        </div>
                                      );
                                    })()
                                  ) : isExpanded ? (
                                    <div className="relative h-4 w-4 flex items-center justify-center">
                                      <FolderOpen
                                        className="h-4 w-4 opacity-100 group-hover/folder-icon:opacity-0 transition-opacity"
                                        style={{
                                          color:
                                            workspace?.metadata?.color ||
                                            "currentColor",
                                        }}
                                      />
                                      <ChevronDown className="h-4 w-4 absolute inset-0 opacity-0 group-hover/folder-icon:opacity-100 transition-opacity text-sidebar-foreground/60" />
                                    </div>
                                  ) : (
                                    <div className="relative h-4 w-4 flex items-center justify-center">
                                      <Folder
                                        className="h-4 w-4 opacity-100 group-hover/folder-icon:opacity-0 transition-opacity"
                                        style={{
                                          color:
                                            workspace?.metadata?.color ||
                                            "currentColor",
                                        }}
                                      />
                                      <ChevronRight className="h-4 w-4 absolute inset-0 opacity-0 group-hover/folder-icon:opacity-100 transition-opacity text-sidebar-foreground/60" />
                                    </div>
                                  )}
                                </button>
                              </CollapsibleTrigger>
                            </ColorIconChooser>

                            {/* Project name - navigates to project page */}
                            <SidebarMenuButton
                              tooltip={workspace.name}
                              onClick={(e) => {
                                if (
                                  e.target.closest(".workspace-menu-trigger")
                                ) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  return;
                                }
                                e.preventDefault();
                                e.stopPropagation();
                                // Navigate directly without expanding the folder
                                // Set a flag to prevent the URL-based expansion effect from expanding
                                localStorage.setItem(
                                  "preventWorkspaceExpansion",
                                  workspace.id
                                );
                                const workspaceObj = workspaces.find(
                                  (w) => w.id === workspace.id
                                );
                                if (workspaceObj) {
                                  setCurrentWorkspaceId(workspace.id);
                                  setCurrentWorkspace(workspaceObj);
                                }
                                clearCurrentThread();
                                localStorage.removeItem(
                                  "threadSelectedFromSidebar"
                                );
                                localStorage.removeItem(
                                  "threadSelectionTimestamp"
                                );
                                localStorage.removeItem(
                                  "workspaceThreadContext"
                                );
                                localStorage.removeItem("lastVisitedThreadId");
                                localStorage.removeItem("pendingWorkspaceChat");
                                localStorage.removeItem("newlyCreatedThreadId");
                                localStorage.setItem(
                                  "intentionalProjectNavigation",
                                  "true"
                                );
                                // Check if we're coming from a chat page - if so, use replace to avoid page reload
                                const isFromChatPage =
                                  location.pathname.startsWith("/chat/");
                                const navigateOptions = isFromChatPage
                                  ? {
                                      replace: true,
                                      state: { preventReload: true },
                                    }
                                  : { state: { preventReload: true } };
                                navigate(
                                  `/projects/${workspace.id}`,
                                  navigateOptions
                                );
                                // Clear the flag after a short delay
                                setTimeout(() => {
                                  localStorage.removeItem(
                                    "preventWorkspaceExpansion"
                                  );
                                }, 100);
                              }}
                              className={cn(
                                "flex-1 min-w-0 max-w-full text-left justify-start",
                                "bg-transparent hover:!bg-transparent hover:!text-sidebar-foreground",
                                isActive && "bg-sidebar-accent"
                              )}
                            >
                              <div className="flex items-center justify-start gap-2 flex-1 min-w-0 text-left">
                                <span
                                  className="truncate min-w-0 text-left"
                                  title={workspace.name}
                                >
                                  {workspace.name}
                                </span>
                                {isDraggingChatOverWorkspace && !isExpanded && (
                                  <span className="text-xs text-purple-500 font-medium flex-shrink-0 animate-pulse">
                                    Drop here
                                  </span>
                                )}
                              </div>
                            </SidebarMenuButton>

                            {/* Workspace Actions Dropdown */}
                            <DropdownMenu
                              open={openWorkspaceDropdownId === workspace.id}
                              onOpenChange={(open) => {
                                if (open) {
                                  setOpenWorkspaceDropdownId(workspace.id);
                                } else {
                                  setOpenWorkspaceDropdownId(null);
                                }
                              }}
                            >
                              <DropdownMenuTrigger asChild>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  aria-label="Project options"
                                  className="workspace-menu-trigger h-6 w-6 flex items-center justify-center bg-transparent rounded-sm opacity-0 group-hover/workspace:opacity-100 group-focus-within/workspace:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:z-[100] outline-none transition-opacity duration-200 hover:bg-sidebar-accent cursor-pointer flex-shrink-0 absolute right-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  <MoreHorizontal
                                    className={cn(
                                      "h-4 w-4 text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
                                    )}
                                  />
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                side="right"
                                alignOffset={-5}
                                sideOffset={5}
                                className="w-[160px] z-[9999] rounded-2xl bg-background/80 backdrop-blur-md border border-border/50"
                                style={{ zIndex: 9999 }}
                                forceMount
                                onClick={(e) => e.stopPropagation()}
                              >
                                <DropdownMenuItem
                                  className="flex cursor-pointer items-center dropdown-menu-item rounded-2xl"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setOpenWorkspaceDropdownId(null);
                                    // Delay opening the ColorIconChooser to allow dropdown to fully close
                                    setTimeout(() => {
                                      setColorIconChooserOpenId(workspace.id);
                                    }, 100);
                                  }}
                                >
                                  <Palette className="h-4 w-4 mr-2" />
                                  Customize
                                </DropdownMenuItem>
                                {/* Manage Team - only for team projects */}
                                {workspace.is_team && (
                                  <DropdownMenuItem
                                    className="flex cursor-pointer items-center dropdown-menu-item rounded-2xl"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setOpenWorkspaceDropdownId(null);
                                      navigate(
                                        `/projects/${workspace.id}?manageTeam=true`
                                      );
                                    }}
                                  >
                                    <Users className="h-4 w-4 mr-2" />
                                    Manage Team
                                  </DropdownMenuItem>
                                )}
                                {/* Hide Rename and Delete for Personal Project */}
                                {!isPersonalProject(workspace) && (
                                  <>
                                    <DropdownMenuItem
                                      className="flex cursor-pointer items-center dropdown-menu-item rounded-2xl"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleWorkspaceRename(e, workspace);
                                        setOpenWorkspaceDropdownId(null);
                                      }}
                                    >
                                      <Edit className="h-4 w-4 mr-2" />
                                      Rename
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="my-1" />
                                    <DropdownMenuItem
                                      className={cn(
                                        "flex cursor-pointer items-center dropdown-menu-item rounded-2xl",
                                        dropdownMenuItemDestructiveClasses
                                      )}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleWorkspaceDelete(e, workspace.id);
                                        setOpenWorkspaceDropdownId(null);
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 mr-2" />
                                      Delete
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* Nested Threads - Pre-rendered for instant display */}
                          <CollapsibleContent
                            forceMount
                            className="data-[state=closed]:hidden"
                          >
                            <WorkspaceThreadsDroppable
                              workspaceId={workspace.id}
                            >
                              <SidebarMenuSub className="!border-sidebar-project-thread-border pl-5">
                                <DropPlaceholder
                                  workspaceId={workspace.id}
                                  isDraggingChatOver={
                                    isDraggingChatOverWorkspace
                                  }
                                />
                                {wsThreads.length === 0 ? (
                                  <SidebarMenuSubItem>
                                    <SidebarMenuSubButton className="text-muted-foreground text-xs italic">
                                      No chats yet
                                    </SidebarMenuSubButton>
                                  </SidebarMenuSubItem>
                                ) : (
                                  <SortableContext
                                    items={wsThreads.map(
                                      (t) => `sidebar-${t.id}`
                                    )}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    {wsThreads.map((thread) => {
                                      const isActiveThread =
                                        location.pathname ===
                                        `/chat/${thread.id}`;
                                      const isNew = isThreadNew(thread.id);
                                      const isBookmarked =
                                        thread.metadata?.is_bookmarked;

                                      return (
                                        <SortableThreadItemInline
                                          key={thread.id}
                                          thread={thread}
                                          workspaceId={workspace.id}
                                        >
                                          <SidebarMenuSubItem>
                                            <div className="relative flex items-center w-full group/thread">
                                              <SidebarMenuSubButton
                                                asChild
                                                className={cn(
                                                  "flex items-center justify-start w-full max-w-full pr-0 cursor-pointer flex-1 text-left",
                                                  isActiveThread &&
                                                    "bg-sidebar-accent font-medium",
                                                  isNew && "font-semibold"
                                                )}
                                              >
                                                <button
                                                  type="button"
                                                  className="text-left justify-start"
                                                  onClick={(e) => {
                                                    if (
                                                      e.target.closest(
                                                        ".dropdown-menu-item"
                                                      ) ||
                                                      e.target.closest(
                                                        ".thread-menu-trigger"
                                                      )
                                                    ) {
                                                      e.preventDefault();
                                                      e.stopPropagation();
                                                      return;
                                                    }
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleThreadSelect(
                                                      e,
                                                      thread.id
                                                    );
                                                  }}
                                                >
                                                  <span
                                                    className="truncate flex-1 min-w-0 text-left"
                                                    title={thread.title}
                                                  >
                                                    {thread.title}
                                                  </span>
                                                  {isNew && (
                                                    <span className="h-1.5 w-1.5 rounded-full bg-primary mr-2 flex-shrink-0" />
                                                  )}
                                                </button>
                                              </SidebarMenuSubButton>

                                              {/* Thread Actions Dropdown */}
                                              <DropdownMenu
                                                open={
                                                  openDropdownId === thread.id
                                                }
                                                onOpenChange={(open) => {
                                                  if (open) {
                                                    setOpenDropdownId(
                                                      thread.id
                                                    );
                                                  } else {
                                                    setOpenDropdownId(null);
                                                  }
                                                }}
                                              >
                                                <DropdownMenuTrigger asChild>
                                                  <div
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Chat options"
                                                    className="thread-menu-trigger h-6 w-6 flex items-center justify-center bg-transparent rounded-sm opacity-0 group-hover/thread:opacity-100 group-focus-within/thread:opacity-100 focus:opacity-100 focus-visible:ring-2 focus-visible:ring-sidebar-ring focus-visible:z-[100] outline-none transition-opacity duration-200 hover:bg-sidebar-accent cursor-pointer flex-shrink-0"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                    }}
                                                  >
                                                    <MoreHorizontal
                                                      className={cn(
                                                        "h-4 w-4 text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
                                                      )}
                                                    />
                                                  </div>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent
                                                  align="end"
                                                  side="right"
                                                  alignOffset={-5}
                                                  sideOffset={5}
                                                  className="w-[200px] z-[9999] rounded-2xl bg-background/80 backdrop-blur-md border border-border/50"
                                                  style={{ zIndex: 9999 }}
                                                  forceMount
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <ThreadMenuContent
                                                    thread={thread}
                                                    workspaces={workspaces}
                                                    isProjectThread={true}
                                                    sourceWorkspaceId={
                                                      workspace.id
                                                    }
                                                    showBookmark={false}
                                                    itemClassName="rounded-2xl"
                                                    onRename={(t) =>
                                                      startRenaming(null, t)
                                                    }
                                                    onMoveToProject={
                                                      handleMoveToProject
                                                    }
                                                    onMoveToChats={(t) =>
                                                      handleMoveToProject(
                                                        t,
                                                        null,
                                                        workspace.id
                                                      )
                                                    }
                                                    onClone={(t) =>
                                                      handleThreadClone(
                                                        null,
                                                        t,
                                                        workspace.id
                                                      )
                                                    }
                                                    onDelete={(t) =>
                                                      handleThreadDelete(
                                                        null,
                                                        t,
                                                        workspace.id
                                                      )
                                                    }
                                                    onClose={() =>
                                                      setOpenDropdownId(null)
                                                    }
                                                  />
                                                </DropdownMenuContent>
                                              </DropdownMenu>
                                            </div>
                                          </SidebarMenuSubItem>
                                        </SortableThreadItemInline>
                                      );
                                    })}
                                  </SortableContext>
                                )}
                              </SidebarMenuSub>
                            </WorkspaceThreadsDroppable>
                          </CollapsibleContent>
                        </SidebarMenuItem>
                      </Collapsible>
                    </SortableWorkspaceItem>
                  );
                })}
              </SortableContext>

              {/* Show More/Less Button */}
              {workspaces.length > 5 && (
                <SidebarMenuItem className="w-full min-w-0 max-w-full">
                  <SidebarMenuButton
                    onClick={() => {
                      // Use startTransition for show more/less toggle (non-urgent)
                      startTransition(() => {
                        setShowAllProjects(!showAllProjects);
                      });
                    }}
                    className="w-full min-w-0 max-w-full text-sidebar-foreground/70"
                  >
                    <MoreHorizontal className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/70" />
                    <span className="truncate min-w-0">
                      {showAllProjects ? "Show less" : "Show more"}
                    </span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </CollapsibleContent>
        </SidebarGroup>
      </Collapsible>
      {/* Dialogs */}
      <ProjectRenameDialog
        isOpen={workspaceRenameDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleWorkspaceRenameCancel();
          }
        }}
        workspaceToRename={workspaceToRename}
        newWorkspaceName={newWorkspaceName}
        setNewWorkspaceName={setNewWorkspaceName}
        isRenamingWorkspace={isRenamingWorkspace}
        onRename={handleWorkspaceRenameSubmit}
        onCancel={handleWorkspaceRenameCancel}
      />
      <ProjectDeleteDialog
        isOpen={workspaceDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleCancelWorkspaceDelete();
          }
        }}
        workspaceToDelete={workspaceToDelete}
        onConfirm={handleConfirmWorkspaceDelete}
        onCancel={handleCancelWorkspaceDelete}
      />
      <WorkspaceCreateDialog
        isOpen={isCreateProjectDialogOpen}
        onOpenChange={setIsCreateProjectDialogOpen}
        onCreateWorkspace={handleCreateWorkspace}
        isLoading={isCreatingProject}
      />
      <ThreadRenameDialog
        isOpen={threadRenameDialogOpen}
        onOpenChange={setThreadRenameDialogOpen}
        threadToRename={threadToRename}
        newThreadName={newThreadName}
        setNewThreadName={setNewThreadName}
        isRenaming={isRenamingThread}
        onRename={handleThreadRenameSubmit}
        onCancel={handleThreadRenameCancel}
      />
    </>
  );
}

// Memoize NavProjects - the component uses useLocation internally for active states
// but preventing parent rerenders (AppSidebar) keeps ScrollArea position stable
// Component to wrap thread list and make entire area droppable
function WorkspaceThreadsDroppable({ workspaceId, children }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `workspace-threads-${workspaceId}`,
    data: {
      type: "workspace",
      workspaceId: workspaceId,
    },
  });

  return (
    <div ref={setNodeRef} className="w-full">
      {children}
    </div>
  );
}

// Component to show drop placeholder at top of thread list
function DropPlaceholder({ workspaceId, isDraggingChatOver }) {
  // Use the isDraggingChatOver prop passed from SortableWorkspaceItem
  // This is more reliable than trying to detect it here
  if (!isDraggingChatOver) {
    return null;
  }

  return (
    <SidebarMenuSubItem>
      <div className="h-8 mx-2 my-1 rounded-md border-2 border-dashed border-purple-400/50 bg-purple-400/10 flex items-center justify-center animate-pulse">
        <span className="text-xs text-purple-500 font-medium">Drop here</span>
      </div>
    </SidebarMenuSubItem>
  );
}

const NavProjectsMemoized = React.memo(NavProjects, (prevProps, nextProps) => {
  // Allow rerenders - child components need to update active states
  return false;
});

export { NavProjectsMemoized as NavProjects };
